/**
 * React error boundary with a retry fallback UI.
 */

import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link ErrorBoundary}. */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/** Captured error + whether a fallback is showing. */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/** Catches render errors and shows a fallback UI. */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  /** Seed the captured-error flag before the first render. */
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  /** Switch to the fallback UI on the next render without mutating the instance. */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  /** Log the error + component stack in development only. */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  /** Custom fallback, then the default retry UI, otherwise children. */
  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center">
          <div className="rounded-full bg-destructive/10 p-4 mb-4">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{getErrorMessage('generic')}</h2>
          <p className="text-muted-foreground mb-4 max-w-md">
            {getErrorMessage('errorBoundaryDetail')}
          </p>
          {this.state.error && (
            <details className="mb-4 text-left max-w-md">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                Détails techniques
              </summary>
              <pre className="mt-2 p-4 bg-muted rounded text-xs overflow-auto">
                {this.state.error.message}
              </pre>
            </details>
          )}
          <Button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              globalThis.location.reload();
            }}
          >
            Recharger la page
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
